import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { CsvImportModal } from '../../components/data/CsvImportModal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { CustomerCreateModal } from './CustomerCreateModal.jsx';

const CSV_COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'creditLimit', label: 'CreditLimit' },
  { key: 'currency', label: 'Currency' },
];

const CSV_FIELDS = [
  { field: 'code', label: 'Code', aliases: ['kundennummer', 'nr'], required: true },
  { field: 'name', label: 'Name', aliases: ['kunde', 'firma'], required: true },
  { field: 'email', label: 'Email', aliases: ['e-mail', 'mail'] },
  { field: 'phone', label: 'Phone', aliases: ['telefon', 'tel'] },
  { field: 'creditLimit', label: 'CreditLimit', aliases: ['kreditlimit', 'limit'] },
  { field: 'currency', label: 'Currency', aliases: ['währung', 'währung'] },
];

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const palette = useChartPalette();
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['sales', 'customers', q],
    queryFn: () => salesApi.listCustomers({ q: q || undefined }),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const { data: insights } = useQuery({
    queryKey: ['sales', 'customer-insights'],
    queryFn: () => salesApi.customerInsights().then((r) => r.items),
  });
  const top = insights?.topCustomers || [];
  const others = Number(insights?.othersRevenue || 0);
  const donutLabels = [...top.map((c) => c.customerName), ...(others > 0 ? [t('sales.customers.insight.others')] : [])];
  const donutValues = [...top.map((c) => Number(c.revenue)), ...(others > 0 ? [others] : [])];
  const donutColors = [
    ...top.map((_, i) => palette?.series?.[i % (palette.series.length || 1)]),
    ...(others > 0 ? [palette?.axis] : []),
  ];

  const columns = [
    { key: 'code',  label: t('col.code') },
    { key: 'name',  label: t('col.name'), render: (r) => <Link to={`/sales/customers/${r.id}`}>{r.name}</Link> },
    { key: 'email', label: t('col.email') },
    {
      key: 'creditLimit',
      label: t('sales.customers.col.creditLimit'),
      align: 'right',
      render: (r) => formatCurrency(r.creditLimit, r.currency),
    },
    { key: 'status', label: t('col.status'), render: (r) => <Badge status={r.status} /> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('sales.customers.title')}</h1>
          <p className="muted">{t('sales.customers.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={data || []} columns={CSV_COLUMNS} filename="kunden" />
          <Button variant="default" onClick={() => setImportOpen(true)}>↑ {t('csv.import')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('sales.customers.action.new')}
          </Button>
        </div>
      </header>

      {insights && insights.total > 0 && (
        <InsightBar
          title={t('sales.customers.insight.revenue')}
          labels={donutLabels}
          values={donutValues}
          colors={donutColors}
          formatValue={(n) => formatCurrency(n)}
          metrics={[
            { label: t('sales.customers.insight.total'), value: insights.total },
            { label: t('sales.customers.insight.active'), value: insights.active },
            { label: t('sales.customers.insight.withOpenOrders'), value: insights.withOpenOrders },
            { label: t('sales.customers.insight.avgRevenue'), value: formatCurrency(insights.avgRevenue) },
          ]}
        />
      )}

      <div className="toolbar">
        <input
          className="input"
          placeholder={t('sales.customers.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('empty.customers')} onRowClick={(r) => navigate(`/sales/customers/${r.id}`)} />
      <CustomerCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('sales.customers.action.new')}
        fields={CSV_FIELDS}
        onImportRow={(row) =>
          salesApi.createCustomer({
            code: row.code,
            name: row.name,
            email: row.email || undefined,
            phone: row.phone || undefined,
            creditLimit: row.creditLimit || '0',
            currency: row.currency || 'CHF',
          })
        }
        onDone={() => queryClient.invalidateQueries({ queryKey: ['sales', 'customers'] })}
      />
    </section>
  );
}
